import { useEffect } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import type { SensorBundle, SensorsResource } from '@mark43/cad-api';
import { useApiClient } from '~/api-client';
import type { SensorsStore } from './sensors-store';

/**
 * Everything the map needs to open, in one call: the sensor directory, the retained alert window,
 * the acting user's archive, and the server's replay clamp. Also re-run on every reconnect to
 * catch up, by `useIntegratedSensors`.
 *
 * This replaced two cold-start calls (`sensors/list` + `sensors/alerts/recent`), one of which
 * reached the vendor. It used to ride on the CAD Next bootstrap as `sensorBundle`; that field is
 * gone. Moving it off login was the point of the endpoint — the bundle's Redis range read is
 * unbounded, and every dispatcher was paying for it at sign-in including at the majority of
 * departments with the feature off.
 *
 * Alerts ingest as **not live**, so a bundle carrying two hundred of them raises no toasts.
 * `archivedAlertKeys` here is narrowed to the alerts in this response, so it goes through
 * `reconcileArchivedAlertKeys` rather than replacing the archived set.
 *
 * Returns an empty bundle rather than nothing when the feature is off for this user, so there is
 * one shape to handle.
 */
export function useSensorBootstrap(store: SensorsStore): UseQueryResult<SensorBundle | null> {
    const apiClient = useApiClient();

    const bootstrapQuery = useQuery({
        queryKey: ['sensor-bootstrap'],
        async queryFn() {
            const response = await apiClient.request<SensorsResource.GetSensorBootstrap>({
                method: 'POST',
                url: 'sensors/bootstrap',
                // An unprovisioned environment 500s here. Degrading to an empty map is better
                // than a global error toast at sign-in.
                shouldFailSilently: true,
            });
            return response.data.data ?? null;
        },
        // Fetched on mount, which is sign-in or the flag turning on (see `SensorsProvider`), and
        // again on each reconnect. Never stale in between: it is an unbounded range scan.
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: 1,
    });
    const bundle = bootstrapQuery.data;

    /*
     * Synced from `data`, not pushed from `onSuccess`.
     *
     * `onSuccess` only runs when a fetch actually happens, and this query is deliberately
     * `cacheTime: Infinity`. A remount while the query sits cached starts from the cached bundle,
     * and a new store instance given only `onSuccess` would stay empty until (or, if that refetch
     * failed, beyond) the next fetch: no cameras on the map, and every sensor panel reporting its
     * sensor unavailable. Reading `data` runs on a cache hit as well as on a fetch.
     *
     * Idempotent by construction, which is what makes re-running it on a cache hit or a
     * reconnect's refetch safe: sensors are replaced, alerts dedup by `alertKey`, and the archive
     * reconcile is a pure function of the response.
     *
     * The alerts are capped at `replayLimit`, so a full page means there is older history this
     * client was not given.
     */
    useEffect(
        function syncSensorBundleIntoStore() {
            if (!bundle) {
                return;
            }
            store.setSensors(bundle.sensors);
            store.setReplayLimit(bundle.replayLimit);
            store.upsertAlerts(bundle.recentAlerts, { isLive: false });
            store.reconcileArchivedAlertKeys(bundle.recentAlerts, bundle.archivedAlertKeys);
            store.setAlertHistoryTruncated(bundle.recentAlerts.length >= bundle.replayLimit);
        },
        [bundle, store]
    );

    return bootstrapQuery;
}
